import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { openCashRegister, closeCashRegister, getTodayCashRegister, getCashRegisterHistory, getCashRegisterBreakdown, sendCashRegisterReport, getBranches, getCompanyConfig, getPrintConfig } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { DollarSign, Clock, CheckCircle, XCircle, AlertTriangle, History, Building2, Lock, Printer, ChevronDown, ChevronUp, Tag, ShoppingBag, Mail } from 'lucide-react';

const money = (n) =>
  `RD$${parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDt = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-DO', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

const DiffBadge = ({ diff }) => {
  if (Math.abs(diff) < 0.01)
    return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 text-sm font-semibold"><CheckCircle className="w-4 h-4" /> Cuadrado</span>;
  if (diff > 0)
    return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 text-sm font-semibold"><AlertTriangle className="w-4 h-4" /> Sobrante {money(diff)}</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-rose-100 text-rose-700 text-sm font-semibold"><XCircle className="w-4 h-4" /> Faltante {money(Math.abs(diff))}</span>;
};

const CashRegister = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [register, setRegister] = useState(null);
  const [history, setHistory] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('today'); // 'today' | 'history'
  const [companyName, setCompanyName] = useState('');
  const [printFormat, setPrintFormat] = useState('letter');

  const [detailModal, setDetailModal] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [showCategories, setShowCategories] = useState(true);
  const [showTailoring, setShowTailoring] = useState(true);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [reportEmail, setReportEmail] = useState('');
  const [sendingReport, setSendingReport] = useState(false);

  // History filters & pagination
  const [histFilters, setHistFilters] = useState({ date_from: '', date_to: '', user_q: '' });
  const [histPage, setHistPage] = useState(1);
  const [histTotal, setHistTotal] = useState(0);
  const [histTotalPages, setHistTotalPages] = useState(1);
  const HIST_PER_PAGE = 20;

  const printRegister = (r, bd = null) => {
    const s = r.summary || {};
    const fmt = (iso) => {
      if (!iso) return '—';
      try { return new Date(iso).toLocaleString('es-DO', { dateStyle: 'short', timeStyle: 'short' }); }
      catch { return iso; }
    };
    const m = (n) => `RD$${parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const diff = s.diferencia ?? 0;
    const empresa = companyName || 'Reporte de caja';
    const now = new Date().toLocaleString('es-DO');

    let html;

    if (printFormat === 'thermal') {
      const sep = '--------------------------------';
      const diffTxt = Math.abs(diff) < 0.01 ? '✔ CUADRADO' : diff > 0 ? `▲ SOBRANTE  ${m(diff)}` : `▼ FALTANTE  ${m(Math.abs(diff))}`;
      const row = (label, val) => `<tr><td>${label}</td><td style="text-align:right">${val}</td></tr>`;
      html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>Cierre de caja</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Courier New', monospace; font-size: 12px; color: #000; width: 300px; margin: 0 auto; padding: 8px 4px; }
    .center { text-align: center; }
    .bold { font-weight: bold; }
    .sep { color: #666; margin: 6px 0; font-size: 11px; }
    table { width: 100%; border-collapse: collapse; }
    td { padding: 2px 0; font-size: 12px; }
    .total-row td { font-weight: bold; border-top: 1px dashed #000; padding-top: 4px; }
    .result { text-align: center; font-weight: bold; font-size: 13px; margin: 6px 0; }
    @media print { @page { size: 80mm auto; margin: 4mm; } body { width: 100%; } }
  </style>
</head>
<body>
  <p class="center bold" style="font-size:14px">${empresa.toUpperCase()}</p>
  <p class="center">Cierre de turno</p>
  <p class="center">${r.branch_name || ''}</p>
  <p class="sep center">${sep}</p>

  <table>
    ${row('Fecha', r.date)}
    ${row('Vendedor', r.opened_by_name || '—')}
    ${row('Apertura', fmt(r.opened_at))}
    ${row('Cierre', r.closed_at ? fmt(r.closed_at) : '—')}
    ${row('1ª venta', fmt(s.primera_venta))}
    ${row('Últ. venta', fmt(s.ultima_venta))}
  </table>

  <p class="sep center">${sep}</p>
  <p class="bold" style="margin-bottom:4px">VENTAS POR MÉTODO</p>
  <table>
    ${row('Efectivo', m(s.total_efectivo))}
    ${row('Tarjeta', m(s.total_tarjeta))}
    ${row('Transferencia', m(s.total_transferencia))}
    ${row('Crédito', m(s.total_credito))}
    <tr class="total-row">${row('TOTAL', m(s.total_general))}</tr>
    ${row('Facturas', s.total_facturas || 0)}
  </table>

  <p class="sep center">${sep}</p>
  <p class="bold" style="margin-bottom:4px">CUADRE DE CAJA</p>
  <table>
    ${row('Fondo inicial', m(r.opening_amount))}
    ${row('Ventas efectivo', m(s.total_efectivo))}
    ${row('Esperado', m(s.efectivo_esperado))}
    ${row('Contado', m(r.closing_amount))}
  </table>
  <p class="result">${diffTxt}</p>

  ${r.closing_notes ? `<p class="sep center">${sep}</p><p style="font-size:11px">Notas: ${r.closing_notes}</p>` : ''}

  ${bd?.categories?.length ? `
  <p class="sep center">${sep}</p>
  <p class="bold" style="margin-bottom:4px">VENTAS POR CATEGORÍA</p>
  <table>
    ${bd.categories.map(c => row(c.category, m(c.total))).join('')}
  </table>` : ''}

  ${bd?.tailoring?.length ? `
  <p class="sep center">${sep}</p>
  <p class="bold" style="margin-bottom:4px">PEDIDOS COBRADOS (${bd.tailoring.length})</p>
  <table>
    ${bd.tailoring.map(t => row(t.tailoring_order_code || t.invoice_number, m(t.total))).join('')}
  </table>` : ''}

  <p class="sep center">${sep}</p>
  <p class="center" style="font-size:10px">Impreso: ${now}</p>
  <script>window.onload = () => { window.print(); window.onafterprint = () => window.close(); };<\/script>
</body>
</html>`;
    } else {
      // Carta 8½ × 11"
      const diffLabel = Math.abs(diff) < 0.01
        ? '<span style="color:#16a34a">✔ Cuadrado</span>'
        : diff > 0
          ? `<span style="color:#ca8a04">▲ Sobrante ${m(diff)}</span>`
          : `<span style="color:#dc2626">▼ Faltante ${m(Math.abs(diff))}</span>`;

      html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>Cierre de caja — ${r.date}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 13px; color: #1e293b; padding: 32px; max-width: 680px; margin: auto; }
    h1 { font-size: 20px; font-weight: 700; margin-bottom: 2px; }
    .sub { color: #64748b; font-size: 12px; margin-bottom: 20px; }
    .section { margin-bottom: 18px; }
    .section-title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; margin-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; }
    td { padding: 5px 0; vertical-align: top; }
    td:last-child { text-align: right; font-weight: 600; font-family: monospace; }
    .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 14px; }
    .kpi { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; }
    .kpi-label { font-size: 11px; color: #64748b; margin-bottom: 3px; }
    .kpi-val { font-size: 15px; font-weight: 700; font-family: monospace; }
    .result { margin-top: 10px; font-size: 14px; font-weight: 600; }
    .notes { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; font-size: 12px; color: #475569; margin-top: 10px; }
    .footer { margin-top: 28px; border-top: 1px solid #e2e8f0; padding-top: 12px; font-size: 11px; color: #94a3b8; text-align: center; }
    @media print { @page { size: letter; margin: 20mm; } body { padding: 0; } }
  </style>
</head>
<body>
  <h1>${empresa}</h1>
  <p class="sub">Cierre de turno — ${r.branch_name || ''} — ${r.date}</p>

  <div class="section">
    <div class="section-title">Información del turno</div>
    <table>
      <tr><td>Vendedor</td><td>${r.opened_by_name || '—'}</td></tr>
      <tr><td>Apertura</td><td>${fmt(r.opened_at)}</td></tr>
      <tr><td>Cierre</td><td>${r.closed_at ? fmt(r.closed_at) : '—'}</td></tr>
      <tr><td>Primera venta</td><td>${fmt(s.primera_venta)}</td></tr>
      <tr><td>Última venta</td><td>${fmt(s.ultima_venta)}</td></tr>
    </table>
  </div>

  <div class="section">
    <div class="section-title">Ventas por método de pago</div>
    <table>
      <tr><td>Efectivo</td><td>${m(s.total_efectivo)}</td></tr>
      <tr><td>Tarjeta</td><td>${m(s.total_tarjeta)}</td></tr>
      <tr><td>Transferencia</td><td>${m(s.total_transferencia)}</td></tr>
      <tr><td>Crédito</td><td>${m(s.total_credito)}</td></tr>
      <tr style="border-top:2px solid #e2e8f0"><td><strong>Total general</strong></td><td style="color:#0891b2">${m(s.total_general)}</td></tr>
      <tr><td style="color:#64748b; font-weight:400">Facturas emitidas</td><td>${s.total_facturas || 0}</td></tr>
    </table>
  </div>

  <div class="section">
    <div class="section-title">Cuadre de caja</div>
    <div class="kpis">
      <div class="kpi"><div class="kpi-label">Fondo inicial</div><div class="kpi-val">${m(r.opening_amount)}</div></div>
      <div class="kpi"><div class="kpi-label">Ventas efectivo</div><div class="kpi-val">${m(s.total_efectivo)}</div></div>
      <div class="kpi"><div class="kpi-label">Esperado</div><div class="kpi-val">${m(s.efectivo_esperado)}</div></div>
      <div class="kpi"><div class="kpi-label">Contado</div><div class="kpi-val">${m(r.closing_amount)}</div></div>
    </div>
    <div class="result">Resultado: ${diffLabel}</div>
    ${r.closing_notes ? `<div class="notes">📝 ${r.closing_notes}</div>` : ''}
  </div>

  ${bd?.categories?.length ? `
  <div class="section">
    <div class="section-title">Ventas por categoría</div>
    <table>
      ${bd.categories.map(c => `<tr><td>${c.category}</td><td>${c.qty} uds.</td><td style="text-align:right;font-family:monospace;font-weight:600">${m(c.total)}</td></tr>`).join('')}
    </table>
  </div>` : ''}

  ${bd?.tailoring?.length ? `
  <div class="section">
    <div class="section-title">Pedidos de confección cobrados (${bd.tailoring.length})</div>
    <table>
      <thead style="background:#f8fafc"><tr>
        <th style="text-align:left;padding:4px 0;font-size:11px;color:#64748b">Pedido</th>
        <th style="text-align:left;padding:4px 0;font-size:11px;color:#64748b">Cliente</th>
        <th style="text-align:left;padding:4px 0;font-size:11px;color:#64748b">Método</th>
        <th style="text-align:right;padding:4px 0;font-size:11px;color:#64748b">Cobrado</th>
      </tr></thead>
      <tbody>
        ${bd.tailoring.map(t => `<tr style="border-bottom:1px solid #e2e8f0">
          <td style="padding:4px 0;font-family:monospace;font-size:11px">${t.tailoring_order_code || t.invoice_number}</td>
          <td style="padding:4px 0">${t.customer_name}</td>
          <td style="padding:4px 0;color:#64748b;text-transform:capitalize">${t.payment_method}</td>
          <td style="padding:4px 0;text-align:right;font-family:monospace;font-weight:600">${m(t.total)}</td>
        </tr>`).join('')}
      </tbody>
    </table>
  </div>` : ''}

  <div class="footer">Impreso el ${now} · ${empresa}</div>
  <script>window.onload = () => { window.print(); window.onafterprint = () => window.close(); };<\/script>
</body>
</html>`;
    }

    const win = window.open('', '_blank', printFormat === 'thermal' ? 'width=360,height=600' : 'width=720,height=700');
    win.document.write(html);
    win.document.close();
  };

  // Open form
  const [openingAmount, setOpeningAmount] = useState('');
  const [opening, setOpening] = useState(false);

  // Close form
  const [closingAmount, setClosingAmount] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  const [closing, setClosing] = useState(false);
  const [showCloseForm, setShowCloseForm] = useState(false);

  useEffect(() => {
    if (isAdmin) getBranches().then(b => setBranches(b || [])).catch(() => {});
    getCompanyConfig().then(d => setCompanyName(d?.name || '')).catch(() => {});
    getPrintConfig().then(d => setPrintFormat(d?.cash_register_print_format || 'letter')).catch(() => {});
  }, [isAdmin]);

  const loadToday = useCallback(async (branchId) => {
    const bid = branchId !== undefined ? branchId : selectedBranch;
    setLoading(true);
    try {
      const data = await getTodayCashRegister(bid);
      setRegister(data?.id ? data : null);
    } catch (err) {
      console.error('Error cargando caja:', err);
      setRegister(null);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  const loadHistory = useCallback(async (branchId, filters, page) => {
    const bid  = branchId !== undefined ? branchId : selectedBranch;
    const filt = filters  !== undefined ? filters  : histFilters;
    const pg   = page     !== undefined ? page     : histPage;
    setLoading(true);
    try {
      const data = await getCashRegisterHistory({ ...filt, branch_id: bid }, pg, HIST_PER_PAGE);
      setHistory(data.items || []);
      setHistTotal(data.total || 0);
      setHistTotalPages(data.total_pages || 1);
    } catch {
      toast.error('Error cargando historial');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (view === 'today') loadToday(selectedBranch);
    else loadHistory(selectedBranch);
  }, [view]); // eslint-disable-line

  const handleBranchChange = (val) => {
    const b = val === 'ALL' ? '' : val;
    setSelectedBranch(b);
    if (view === 'today') loadToday(b);
    else { setHistPage(1); loadHistory(b, histFilters, 1); }
  };

  const handleHistFilterChange = (key, val) => {
    const next = { ...histFilters, [key]: val };
    setHistFilters(next);
    setHistPage(1);
    loadHistory(selectedBranch, next, 1);
  };

  const handleHistPageChange = (pg) => {
    setHistPage(pg);
    loadHistory(selectedBranch, histFilters, pg);
  };

  const openDetailModal = async (r) => {
    setDetailModal(r);
    setBreakdown(null);
    setBreakdownLoading(true);
    setShowEmailForm(false);
    setReportEmail('');
    try {
      const data = await getCashRegisterBreakdown(r.id);
      setBreakdown(data);
    } catch {
      toast.error('No se pudo cargar el desglose');
    } finally {
      setBreakdownLoading(false);
    }
  };

  const clearHistFilters = () => {
    const empty = { date_from: '', date_to: '', user_q: '' };
    setHistFilters(empty);
    setHistPage(1);
    loadHistory(selectedBranch, empty, 1);
  };

  const handleOpen = async () => {
    const amount = parseFloat(openingAmount || 0);
    if (isNaN(amount) || amount < 0) { toast.error('Ingresa un monto válido'); return; }
    setOpening(true);
    try {
      const data = await openCashRegister({ opening_amount: amount });
      setRegister(data);
      setOpeningAmount('');
      toast.success('Turno abierto correctamente');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error abriendo turno');
    } finally {
      setOpening(false);
    }
  };

  const handleClose = async () => {
    const amount = parseFloat(closingAmount || 0);
    if (isNaN(amount) || amount < 0) { toast.error('Ingresa el monto contado'); return; }
    setClosing(true);
    try {
      const data = await closeCashRegister({ closing_amount: amount, closing_notes: closingNotes });
      setRegister(data);
      setShowCloseForm(false);
      setClosingAmount('');
      setClosingNotes('');
      toast.success('Turno cerrado. Reporte enviado al administrador.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error cerrando turno');
    } finally {
      setClosing(false);
    }
  };

  const summary = register?.summary || {};
  const diferencia = summary.diferencia ?? 0;
  const todayStr = new Date().toISOString().slice(0, 10);
  const isStale = register?.status === 'open' && register?.date && register.date < todayStr;

  return (
    <div className="p-6 md:p-8 space-y-6">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Caja</h1>
          <p className="text-slate-500 text-sm mt-1">Control de turno y cuadre de caja</p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-400" />
              <Select value={selectedBranch || 'ALL'} onValueChange={handleBranchChange}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Todas las sucursales" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todas</SelectItem>
                  {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={() => view === 'today' ? loadToday() : loadHistory()}
            className="text-slate-600">
            ↻ Actualizar
          </Button>
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            <button onClick={() => setView('today')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${view === 'today' ? 'bg-cyan-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
              <Clock className="w-4 h-4 inline mr-1" /> Hoy
            </button>
            <button onClick={() => setView('history')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${view === 'history' ? 'bg-cyan-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
              <History className="w-4 h-4 inline mr-1" /> Historial
            </button>
          </div>
        </div>
      </div>

      {/* TODAY VIEW */}
      {view === 'today' && (
        <>
          {loading ? (
            <div className="flex items-center justify-center h-48 text-slate-400">Cargando...</div>
          ) : !register?.id ? (
            /* No register today — open form */
            <Card className="bg-white border border-slate-200 shadow-sm max-w-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <DollarSign className="w-5 h-5 text-cyan-500" />
                  Apertura de caja
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-500">No hay turno abierto hoy. Declara el fondo inicial para comenzar.</p>
                <div className="space-y-2">
                  <Label>Fondo inicial (efectivo en caja)</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={openingAmount}
                    onChange={e => setOpeningAmount(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleOpen()}
                    placeholder="0.00"
                    className="text-lg font-mono"
                    autoFocus
                  />
                </div>
                <Button onClick={handleOpen} disabled={opening}
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-white">
                  <DollarSign className="w-4 h-4 mr-2" />
                  {opening ? 'Abriendo...' : 'Abrir turno'}
                </Button>
              </CardContent>
            </Card>

          ) : register.status === 'open' ? (
            /* Open register */
            <div className="space-y-6">
              {/* Stale turn warning */}
              {isStale && (
                <div className="flex items-start gap-3 p-4 bg-rose-50 border border-rose-300 rounded-xl">
                  <AlertTriangle className="w-6 h-6 text-rose-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-rose-700">Turno del día anterior sin cerrar</p>
                    <p className="text-sm text-rose-600 mt-0.5">
                      Tienes un turno abierto del <strong>{register.date}</strong> por <strong>{register.opened_by_name}</strong>.
                      Debes cerrarlo para poder operar hoy.
                    </p>
                  </div>
                </div>
              )}

              {/* Status banner */}
              {!isStale && (
                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-green-700">Turno abierto</p>
                    <p className="text-sm text-green-600">
                      Abierto por <strong>{register.opened_by_name}</strong> a las {fmtDt(register.opened_at)}
                    </p>
                  </div>
                </div>
              )}

              {/* KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardContent className="pt-4">
                    <p className="text-xs text-slate-500 mb-1">Fondo inicial</p>
                    <p className="text-lg font-bold text-slate-700" style={{ fontFamily: 'JetBrains Mono' }}>
                      {money(register.opening_amount)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardContent className="pt-4">
                    <p className="text-xs text-slate-500 mb-1">Ventas en efectivo</p>
                    <p className="text-lg font-bold text-cyan-600" style={{ fontFamily: 'JetBrains Mono' }}>
                      {money(summary.total_efectivo)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardContent className="pt-4">
                    <p className="text-xs text-slate-500 mb-1">Total general</p>
                    <p className="text-lg font-bold text-slate-700" style={{ fontFamily: 'JetBrains Mono' }}>
                      {money(summary.total_general)}
                    </p>
                    <p className="text-xs text-slate-400">{summary.total_facturas || 0} facturas</p>
                  </CardContent>
                </Card>
              </div>

              {/* Close form */}
              {(
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                      <Lock className="w-5 h-5 text-slate-500" />
                      Cierre de caja
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {!showCloseForm && !isStale ? (
                      <Button variant="outline" onClick={() => setShowCloseForm(true)}
                        className="border-rose-200 text-rose-600 hover:bg-rose-50">
                        Cerrar turno
                      </Button>
                    ) : (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Efectivo contado en caja</Label>
                          <Input type="number" min="0" step="0.01"
                            value={closingAmount}
                            onChange={e => setClosingAmount(e.target.value)}
                            placeholder="0.00" className="text-lg font-mono" autoFocus />
                          <p className="text-xs text-slate-500">
                            Efectivo esperado: <strong>{money((register.opening_amount || 0) + (summary.total_efectivo || 0))}</strong>
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label>Notas (opcional)</Label>
                          <Input value={closingNotes} onChange={e => setClosingNotes(e.target.value)}
                            placeholder="Observaciones del cierre..." />
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => setShowCloseForm(false)}>Cancelar</Button>
                          <Button onClick={handleClose} disabled={closing}
                            className="bg-rose-500 hover:bg-rose-600 text-white">
                            <Lock className="w-4 h-4 mr-2" />
                            {closing ? 'Cerrando...' : 'Confirmar cierre'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

          ) : (
            /* Closed register — show summary */
            <div className="space-y-6">
              <div className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                <Lock className="w-6 h-6 text-slate-400 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-slate-700">Turno cerrado</p>
                  <p className="text-sm text-slate-500">
                    Cerrado por <strong>{register.closed_by_name}</strong> a las {fmtDt(register.closed_at)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Timing */}
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                      <Clock className="w-4 h-4 text-cyan-500" /> Horarios
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full text-sm">
                      <tbody className="space-y-2">
                        {[
                          ['Apertura', fmtDt(register.opened_at)],
                          ['Cierre', fmtDt(register.closed_at)],
                          ['Primera venta', fmtDt(summary.primera_venta)],
                          ['Última venta', fmtDt(summary.ultima_venta)],
                        ].map(([label, val]) => (
                          <tr key={label} className="border-b last:border-0">
                            <td className="py-2 text-slate-500">{label}</td>
                            <td className="py-2 text-right font-medium">{val}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                {/* Sales breakdown */}
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                      <DollarSign className="w-4 h-4 text-cyan-500" /> Ventas del turno
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full text-sm">
                      <tbody>
                        {[
                          ['Efectivo', summary.total_efectivo],
                          ['Tarjeta', summary.total_tarjeta],
                          ['Transferencia', summary.total_transferencia],
                          ['Crédito', summary.total_credito],
                        ].map(([label, val]) => (
                          <tr key={label} className="border-b">
                            <td className="py-2 text-slate-500">{label}</td>
                            <td className="py-2 text-right font-mono">{money(val)}</td>
                          </tr>
                        ))}
                        <tr className="font-bold">
                          <td className="py-2">Total general</td>
                          <td className="py-2 text-right font-mono text-cyan-600">{money(summary.total_general)}</td>
                        </tr>
                        <tr>
                          <td className="py-2 text-slate-500">Facturas emitidas</td>
                          <td className="py-2 text-right">{summary.total_facturas}</td>
                        </tr>
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                {/* Cash reconciliation */}
                <Card className="bg-white border border-slate-200 shadow-sm lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                      <CheckCircle className="w-4 h-4 text-cyan-500" /> Cuadre de caja
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                      {[
                        ['Fondo inicial', money(register.opening_amount)],
                        ['Ventas efectivo', money(summary.total_efectivo)],
                        ['Efectivo esperado', money(summary.efectivo_esperado)],
                        ['Efectivo contado', money(register.closing_amount)],
                      ].map(([label, val]) => (
                        <div key={label} className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs text-slate-500">{label}</p>
                          <p className="text-lg font-bold font-mono mt-1">{val}</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-600">Resultado:</span>
                      <DiffBadge diff={diferencia} />
                    </div>
                    {register.closing_notes && (
                      <p className="mt-3 text-sm text-slate-500 bg-slate-50 rounded p-2">
                        📝 {register.closing_notes}
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Open new turno */}
                <Card className="bg-white border border-slate-200 shadow-sm max-w-md">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                      <DollarSign className="w-5 h-5 text-cyan-500" />
                      Abrir nuevo turno
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-slate-500">El turno anterior fue cerrado. Puedes abrir un nuevo turno.</p>
                    <div className="space-y-2">
                      <Label>Fondo inicial (efectivo en caja)</Label>
                      <Input
                        type="number" min="0" step="0.01"
                        value={openingAmount}
                        onChange={e => setOpeningAmount(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleOpen()}
                        placeholder="0.00"
                        className="text-lg font-mono"
                      />
                    </div>
                    <Button onClick={handleOpen} disabled={opening}
                      className="w-full bg-cyan-500 hover:bg-cyan-600 text-white">
                      <DollarSign className="w-4 h-4 mr-2" />
                      {opening ? 'Abriendo...' : 'Abrir nuevo turno'}
                    </Button>
                  </CardContent>
                </Card>

              </div>
            </div>
          )}
        </>
      )}

      {/* HISTORY VIEW */}
      {view === 'history' && (
        <div className="space-y-4">
          {/* Filtros */}
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-500">Desde</label>
                  <Input type="date" className="w-36 text-sm"
                    value={histFilters.date_from}
                    onChange={e => handleHistFilterChange('date_from', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-500">Hasta</label>
                  <Input type="date" className="w-36 text-sm"
                    value={histFilters.date_to}
                    onChange={e => handleHistFilterChange('date_to', e.target.value)} />
                </div>
                {isAdmin && (
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-slate-500">Vendedor</label>
                    <Input className="w-44 text-sm" placeholder="Buscar por nombre..."
                      value={histFilters.user_q}
                      onChange={e => handleHistFilterChange('user_q', e.target.value)} />
                  </div>
                )}
                {(histFilters.date_from || histFilters.date_to || histFilters.user_q) && (
                  <Button variant="outline" size="sm" onClick={clearHistFilters}
                    className="text-slate-500 self-end">
                    Limpiar filtros
                  </Button>
                )}
                <span className="text-xs text-slate-400 self-end ml-auto">
                  {histTotal} {histTotal === 1 ? 'resultado' : 'resultados'}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Tabla */}
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="pt-4">
              {loading ? (
                <div className="h-32 flex items-center justify-center text-slate-400">Cargando...</div>
              ) : history.length === 0 ? (
                <div className="h-32 flex items-center justify-center text-slate-400">No hay turnos registrados</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 font-medium text-slate-600">Fecha</th>
                        {isAdmin && <th className="text-left p-3 font-medium text-slate-600">Sucursal</th>}
                        <th className="text-left p-3 font-medium text-slate-600">Vendedor</th>
                        <th className="text-left p-3 font-medium text-slate-600">Apertura</th>
                        <th className="text-left p-3 font-medium text-slate-600">Cierre</th>
                        <th className="text-right p-3 font-medium text-slate-600">Fondo</th>
                        <th className="text-right p-3 font-medium text-slate-600">Efectivo</th>
                        <th className="text-right p-3 font-medium text-slate-600">Total</th>
                        <th className="text-center p-3 font-medium text-slate-600">Cuadre</th>
                        <th className="text-center p-3 font-medium text-slate-600">Estado</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((r) => {
                        const s = r.summary || {};
                        const diff = s.diferencia ?? null;
                        return (
                          <tr key={r.id} className="border-b hover:bg-slate-50">
                            <td className="p-3">{r.date}</td>
                            {isAdmin && <td className="p-3 text-slate-600">{r.branch_name}</td>}
                            <td className="p-3 text-slate-600">{r.opened_by_name}</td>
                            <td className="p-3 text-slate-500 text-xs">{fmtDt(r.opened_at)}</td>
                            <td className="p-3 text-slate-500 text-xs">{r.closed_at ? fmtDt(r.closed_at) : '—'}</td>
                            <td className="p-3 text-right font-mono">{money(r.opening_amount)}</td>
                            <td className="p-3 text-right font-mono">{money(s.total_efectivo)}</td>
                            <td className="p-3 text-right font-mono font-bold">{money(s.total_general)}</td>
                            <td className="p-3 text-center">
                              {diff !== null ? <DiffBadge diff={diff} /> : '—'}
                            </td>
                            <td className="p-3 text-center">
                              {r.status === 'open'
                                ? <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">Abierto</span>
                                : <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">Cerrado</span>
                              }
                            </td>
                            <td className="p-3 text-center">
                              <button onClick={() => openDetailModal(r)}
                                className="text-cyan-500 hover:text-cyan-700 text-xs font-medium underline">
                                Ver detalles
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Paginación */}
              {histTotalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                  <span className="text-xs text-slate-500">
                    Página {histPage} de {histTotalPages}
                  </span>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" disabled={histPage === 1}
                      onClick={() => handleHistPageChange(histPage - 1)}>
                      ‹ Anterior
                    </Button>
                    {Array.from({ length: histTotalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === histTotalPages || Math.abs(p - histPage) <= 1)
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, i) => p === '…'
                        ? <span key={`ellipsis-${i}`} className="px-2 py-1 text-slate-400 text-sm">…</span>
                        : <Button key={p} variant={p === histPage ? 'default' : 'outline'} size="sm"
                            className={p === histPage ? 'bg-cyan-500 hover:bg-cyan-600 text-white' : ''}
                            onClick={() => handleHistPageChange(p)}>
                            {p}
                          </Button>
                      )
                    }
                    <Button variant="outline" size="sm" disabled={histPage === histTotalPages}
                      onClick={() => handleHistPageChange(histPage + 1)}>
                      Siguiente ›
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      {/* Detail Modal */}
      {detailModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                Detalle del turno
              </h2>
              <button onClick={() => setDetailModal(null)} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="p-6 space-y-4">

              {/* Status banner */}
              <div className={`flex items-center gap-3 p-4 rounded-xl border ${detailModal.status === 'open' ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                {detailModal.status === 'open'
                  ? <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  : <Lock className="w-5 h-5 text-slate-400 flex-shrink-0" />}
                <div>
                  <p className={`font-semibold ${detailModal.status === 'open' ? 'text-green-700' : 'text-slate-700'}`}>
                    {detailModal.status === 'open' ? 'Turno abierto' : 'Turno cerrado'}
                  </p>
                  <p className="text-sm text-slate-500">
                    {detailModal.status === 'open'
                      ? `Abierto por ${detailModal.opened_by_name} a las ${fmtDt(detailModal.opened_at)}`
                      : `Cerrado por ${detailModal.closed_by_name || detailModal.opened_by_name} a las ${fmtDt(detailModal.closed_at)}`}
                  </p>
                </div>
              </div>

              {/* KPIs */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white border border-slate-200 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Fondo inicial</p>
                  <p className="text-lg font-bold font-mono mt-1">{money(detailModal.opening_amount)}</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Efectivo ventas</p>
                  <p className="text-lg font-bold font-mono text-cyan-600 mt-1">{money(detailModal.summary?.total_efectivo)}</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Total general</p>
                  <p className="text-lg font-bold font-mono mt-1">{money(detailModal.summary?.total_general)}</p>
                  <p className="text-xs text-slate-400">{detailModal.summary?.total_facturas || 0} facturas</p>
                </div>
              </div>

              {detailModal.summary && (
                <div className="grid grid-cols-1 gap-4">
                  {/* Horarios */}
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-700 mb-3 text-sm flex items-center gap-2">
                      <Clock className="w-4 h-4 text-cyan-500" /> Horarios
                    </h3>
                    <table className="w-full text-sm">
                      <tbody>
                        {[
                          ['Apertura', fmtDt(detailModal.opened_at)],
                          ['Cierre', detailModal.closed_at ? fmtDt(detailModal.closed_at) : '—'],
                          ['Primera venta', fmtDt(detailModal.summary.primera_venta)],
                          ['Última venta', fmtDt(detailModal.summary.ultima_venta)],
                        ].map(([label, val]) => (
                          <tr key={label} className="border-b last:border-0">
                            <td className="py-1.5 text-slate-500">{label}</td>
                            <td className="py-1.5 text-right font-medium">{val}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Ventas por método */}
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-700 mb-3 text-sm flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-cyan-500" /> Ventas del turno
                    </h3>
                    <table className="w-full text-sm">
                      <tbody>
                        {[
                          ['Efectivo', money(detailModal.summary.total_efectivo)],
                          ['Tarjeta', money(detailModal.summary.total_tarjeta)],
                          ['Transferencia', money(detailModal.summary.total_transferencia)],
                          ['Crédito', money(detailModal.summary.total_credito)],
                        ].map(([label, val]) => (
                          <tr key={label} className="border-b">
                            <td className="py-1.5 text-slate-500">{label}</td>
                            <td className="py-1.5 text-right font-mono">{val}</td>
                          </tr>
                        ))}
                        <tr className="font-bold">
                          <td className="py-1.5">Total general</td>
                          <td className="py-1.5 text-right font-mono text-cyan-600">{money(detailModal.summary.total_general)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Cuadre */}
                  {detailModal.status === 'closed' && (
                    <div className="bg-white border border-slate-200 rounded-lg p-4">
                      <h3 className="font-semibold text-slate-700 mb-3 text-sm flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-cyan-500" /> Cuadre de caja
                      </h3>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        {[
                          ['Fondo inicial', money(detailModal.opening_amount)],
                          ['Ventas efectivo', money(detailModal.summary.total_efectivo)],
                          ['Esperado', money(detailModal.summary.efectivo_esperado)],
                          ['Contado', money(detailModal.closing_amount)],
                        ].map(([label, val]) => (
                          <div key={label} className="bg-slate-50 rounded p-2">
                            <p className="text-xs text-slate-500">{label}</p>
                            <p className="font-bold font-mono text-sm mt-0.5">{val}</p>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-600">Resultado:</span>
                        <DiffBadge diff={detailModal.summary.diferencia ?? 0} />
                      </div>
                      {detailModal.closing_notes && (
                        <p className="mt-3 text-sm text-slate-500 bg-slate-50 rounded p-2">📝 {detailModal.closing_notes}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Breakdown */}
              {breakdownLoading && (
                <div className="flex items-center justify-center py-6 text-slate-400 text-sm">Cargando desglose...</div>
              )}

              {breakdown && !breakdownLoading && (
                <div className="space-y-3">

                  {/* Categorías */}
                  {breakdown.categories?.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setShowCategories(v => !v)}
                        className="w-full flex items-center justify-between p-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-cyan-500" /> Ventas por categoría
                        </span>
                        {showCategories ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </button>
                      {showCategories && (
                        <table className="w-full text-sm border-t border-slate-100">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium text-slate-500">Categoría</th>
                              <th className="text-right px-3 py-2 font-medium text-slate-500">Unidades</th>
                              <th className="text-right px-3 py-2 font-medium text-slate-500">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {breakdown.categories.map(c => (
                              <tr key={c.category} className="border-t border-slate-100">
                                <td className="px-3 py-2 text-slate-700">{c.category}</td>
                                <td className="px-3 py-2 text-right text-slate-500">{c.qty}</td>
                                <td className="px-3 py-2 text-right font-mono font-semibold">{money(c.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                  {/* Pedidos de confección */}
                  {breakdown.tailoring?.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setShowTailoring(v => !v)}
                        className="w-full flex items-center justify-between p-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <ShoppingBag className="w-4 h-4 text-cyan-500" /> Pedidos de confección ({breakdown.tailoring.length})
                        </span>
                        {showTailoring ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </button>
                      {showTailoring && (
                        <table className="w-full text-sm border-t border-slate-100">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium text-slate-500">Pedido</th>
                              <th className="text-left px-3 py-2 font-medium text-slate-500">Cliente</th>
                              <th className="text-left px-3 py-2 font-medium text-slate-500">Método</th>
                              <th className="text-right px-3 py-2 font-medium text-slate-500">Cobrado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {breakdown.tailoring.map(t => (
                              <tr key={t.invoice_number} className="border-t border-slate-100">
                                <td className="px-3 py-2 font-mono text-xs text-slate-600">{t.tailoring_order_code || t.invoice_number}</td>
                                <td className="px-3 py-2 text-slate-700">{t.customer_name}</td>
                                <td className="px-3 py-2 text-slate-500 capitalize">{t.payment_method}</td>
                                <td className="px-3 py-2 text-right font-mono font-semibold">{money(t.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                </div>
              )}
            </div>
            <div className="border-t">
              {/* Email form inline */}
              {detailModal.status === 'closed' && showEmailForm && (
                <div className="px-4 pt-3 pb-2 flex items-center gap-2">
                  <Input
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={reportEmail}
                    onChange={e => setReportEmail(e.target.value)}
                    onKeyDown={async e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!reportEmail || !reportEmail.includes('@')) { toast.error('Correo inválido'); return; }
                        setSendingReport(true);
                        try {
                          await sendCashRegisterReport(detailModal.id, reportEmail);
                          toast.success(`Reporte enviado a ${reportEmail}`);
                          setShowEmailForm(false);
                          setReportEmail('');
                        } catch (err) {
                          toast.error(err.response?.data?.error || 'Error al enviar');
                        } finally { setSendingReport(false); }
                      }
                    }}
                    className="flex-1 text-sm"
                    autoFocus
                  />
                  <Button size="sm" disabled={sendingReport}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white shrink-0"
                    onClick={async () => {
                      if (!reportEmail || !reportEmail.includes('@')) { toast.error('Correo inválido'); return; }
                      setSendingReport(true);
                      try {
                        await sendCashRegisterReport(detailModal.id, reportEmail);
                        toast.success(`Reporte enviado a ${reportEmail}`);
                        setShowEmailForm(false);
                        setReportEmail('');
                      } catch (err) {
                        toast.error(err.response?.data?.error || 'Error al enviar');
                      } finally { setSendingReport(false); }
                    }}>
                    {sendingReport ? 'Enviando...' : 'Enviar'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setShowEmailForm(false); setReportEmail(''); }}
                    className="text-slate-400 shrink-0">✕</Button>
                </div>
              )}
              <div className="p-4 flex justify-between items-center">
                {detailModal.status === 'closed' ? (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => printRegister(detailModal, breakdown)}
                      className="text-slate-600 border-slate-300">
                      <Printer className="w-4 h-4 mr-2" /> Imprimir
                    </Button>
                    <Button variant="outline" onClick={() => setShowEmailForm(v => !v)}
                      className={`border-slate-300 ${showEmailForm ? 'bg-slate-100 text-slate-700' : 'text-slate-600'}`}>
                      <Mail className="w-4 h-4 mr-2" /> Enviar por correo
                    </Button>
                  </div>
                ) : <span />}
                <Button onClick={() => setDetailModal(null)}>Cerrar</Button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CashRegister;